import { GridColDef } from '@mui/x-data-grid';
import { RslDimension } from '../../../api/schema';
import { formatters } from '@utils/formattersV2';
import { MISSING_VALUE } from '@constants/stats';
import {
  OptimizationSelectProps,
  Option as BaseOption,
} from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/OptimizationSelectV2';
import { sanitizeOptimization } from '@utils/valueTransformers';
import { OptimizationIndexChip } from '@features/campaigns/components/OptimizationIndexChip';
import { OptimizationSelectV2 } from '@features/campaigns/components/RealTimeOptimization/ComparisonByDimension/OptimizationSelectV2';
import { sharedColumns } from './shared-columns';
import { liftWithConfidenceText } from '@features/campaigns/domain/text/lift-with-confidence-text';

export type OptimizationIndex = keyof Pick<
  RslDimension,
  | 'incrementalVisitLiftIndex'
  | 'incrementalVisitsPerImpressionIndex'
  | 'totalVisitsPerImpressionIndex'
>;

type Option = BaseOption & {
  value: OptimizationIndex;
};

const optimizationIndexOptions: Record<Option['value'], Option> = {
  incrementalVisitLiftIndex: {
    value: 'incrementalVisitLiftIndex',
    name: 'Opt. Index - Incr. Visits Lift',
  },
  incrementalVisitsPerImpressionIndex: {
    value: 'incrementalVisitsPerImpressionIndex',
    name: 'Opt. Index - Incr. Visits per Impression',
  },
  totalVisitsPerImpressionIndex: {
    value: 'totalVisitsPerImpressionIndex',
    name: 'Opt. Index - Total Visits per Impression',
  },
};

type ColumnsOptions = {
  optimizationIndex: OptimizationIndex;
  setOptimizationIndex: OptimizationSelectProps<Option>['setSelectedOptimization'];
};

export const buildVisitColumns = (
  options: ColumnsOptions
): GridColDef<RslDimension>[] => [
  sharedColumns.statisticallySignificant,
  sharedColumns.dimensionValue,
  {
    field: 'optimizationIndex',
    headerName: 'Optimization Index',
    display: 'flex',
    flex: 1,
    minWidth: 400,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (_, row) =>
      sanitizeOptimization(row[options.optimizationIndex]),
    renderHeader: () => (
      <OptimizationSelectV2
        selectedOptimization={
          optimizationIndexOptions[options.optimizationIndex]
        }
        setSelectedOptimization={options.setOptimizationIndex}
        options={Object.values(optimizationIndexOptions)}
      />
    ),
    renderCell: ({ row }) => (
      <OptimizationIndexChip
        value={sanitizeOptimization(row[options.optimizationIndex])}
      />
    ),
  },
  sharedColumns.impressions,
  sharedColumns.uniqueUsers,
  {
    field: 'totalProjectedVisits',
    headerName: 'Total Visits',
    display: 'flex',
    flex: 1,
    minWidth: 150,
    type: 'number',
    valueFormatter: value => formatters.visits(value),
  },
  {
    field: 'incrementalVisitLift',
    headerName: 'Incr. Visit Lift',
    display: 'flex',
    flex: 1.2,
    minWidth: 220,
    type: 'number',
    valueFormatter: (_, row) =>
      row.statisticallySignificant
        ? liftWithConfidenceText(
            row.incrementalVisitLift,
            row.incrementalVisitLiftConfidenceLevel
          )
        : MISSING_VALUE,
  },
  {
    field: 'projectedIncrementalVisits',
    headerName: 'Incr. Visits',
    display: 'flex',
    flex: 1,
    minWidth: 150,
    type: 'number',
    valueFormatter: value => formatters.visits(value, { full: true }),
  },
];
